/**
 * The scenes — the computed SVG drawings of the story — stay the
 * script's (ADR-0003 § 2). The server lays their holders out, empty, with
 * `data-scene="<name>"` (or `"<name>:<argument>"`); this draws each one at
 * boot, before the motion looks for its layers, as the first thing in its
 * holder.
 *
 * The drawings are decorative (`aria-hidden`): without the script, a page
 * keeps its text and loses its pictures.
 *
 * @module lib/scenes
 */

import { $$ } from './dom.js';
import { renderToString } from './html.js';
import { factoryMachines, factoryWall } from '../scenes/factory.js';
import { finaleBack, finaleFront } from '../scenes/finale.js';
import { hoodie } from '../scenes/hoodie.js';
import { back, front } from '../scenes/opening.js';
import { lamp, skyline } from '../scenes/street.js';

/** @type {Record<string, (argument?: string) => import('./html.js').Markup>} */
const SCENES = {
    'opening-front': front,
    'opening-back': back,
    // The argument prefixes its clip paths: unique per page.
    hoodie: (id) => hoodie(id ?? 'hoodie'),
    skyline,
    lamp,
    'factory-wall': factoryWall,
    'factory-machines': factoryMachines,
    'finale-front': finaleFront,
    'finale-back': finaleBack,
};

/** Draws every scene the page lays out. */
export function drawScenes() {
    $$('[data-scene]').forEach((holder) => {
        const [name, argument] = holder.dataset.scene.split(':');
        const draw = SCENES[name];

        if (!draw) {
            console.error('[scenes]', `Unknown scene: ${name}`);
            return;
        }

        holder.insertAdjacentHTML('afterbegin', renderToString(draw(argument)));
    });
}
